import * as React from "react";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";

import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import RateReviewOutlined from "@mui/icons-material/RateReviewOutlined";
import MarkChatReadOutlined from "@mui/icons-material/MarkChatReadOutlined";
import ScheduleOutlined from "@mui/icons-material/ScheduleOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import TaskAlt from "@mui/icons-material/TaskAlt";
import MenuBookOutlined from "@mui/icons-material/MenuBookOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import HighlightOff from "@mui/icons-material/HighlightOff";
import SyncOutlined from "@mui/icons-material/SyncOutlined";
import PublishOutlined from "@mui/icons-material/PublishOutlined";
import PauseCircleOutline from "@mui/icons-material/PauseCircleOutline";
import BlockOutlined from "@mui/icons-material/BlockOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import ForumOutlined from "@mui/icons-material/ForumOutlined";
import HelpOutline from "@mui/icons-material/HelpOutline";
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';

import { Localized } from "@fluent/react";

type StatusConfigEntry = {
    labelId: string;
    fallback: string;
    color: string;
    iconColor?: string;
    icon: typeof DescriptionOutlined;
};

type DocumentStatusProps = {
    status: string | null | undefined;
    size?: "small" | "medium";
    fullWidth?: boolean;
    maxWidth?: number | string;
    truncate?: boolean;
    className?: string;
};

// color: tło i ramka; iconColor: opcjonalny mocniejszy kolor ikony.
const STATUS_CONFIG = {
    N: {
        labelId: "document-status-n",
        fallback: "_Nowy",
        color: "#0288D1",
        iconColor: "#0277BD",
        icon: DescriptionOutlined,
    },
    O: {
        labelId: "document-status-o",
        fallback: "_Opiniowanie",
        color: "#CA8A04",
        iconColor: "#EF6C00",
        icon: QuestionAnswerIcon,
    },
    D: {
        labelId: "document-status-d",
        fallback: "_Zaopiniowany",
        color: "#17857F",
        iconColor: "#127872",
        icon: MarkChatReadOutlined,
    },
    I: {
        labelId: "document-status-i",
        fallback: "_Zatwierdzanie",
        color: "#009EAA",
        iconColor: "#007D87",
        icon: ScheduleOutlined,
    },
    A: {
        labelId: "document-status-a",
        fallback: "_Zatwierdzony",
        color: "#138A43",
        icon: CheckCircleOutline,
    },
    F: {
        labelId: "document-status-f",
        fallback: "_Zakończony",
        color: "#35645A",
        icon: TaskAlt,
    },
    P: {
        labelId: "document-status-p",
        fallback: "_Opublikowany",
        color: "#315E8A",
        icon: MenuBookOutlined,
    },
    K: {
        labelId: "document-status-k",
        fallback: "_Korekta",
        color: "#C26A14",
        icon: EditOutlined,
    },
    E: {
        labelId: "document-status-e",
        fallback: "_Zaimportowany",
        color: "#39788A",
        icon: FileDownloadOutlined,
    },
    R: {
        labelId: "document-status-r",
        fallback: "_Odrzucony",
        color: "#CE2626",
        iconColor: "#C51F1F",
        icon: HighlightOff,
    },
    X: {
        labelId: "document-status-x",
        fallback: "_Importowanie",
        color: "#CA8A04",
        iconColor: "#A26B00",
        icon: SyncOutlined,
    },
    B: {
        labelId: "document-status-b",
        fallback: "_Gotowy do publikacji",
        color: "#526DAA",
        icon: PublishOutlined,
    },
    H: {
        labelId: "document-status-h",
        fallback: "_Wstrzymany",
        color: "#697783",
        icon: PauseCircleOutline,
    },
    C: {
        labelId: "document-status-c",
        fallback: "_Anulowany",
        color: "#5F6B76",
        icon: BlockOutlined,
    },
    W: {
        labelId: "document-status-w",
        fallback: "_Zatwierdzony z zastrzeżeniami",
        color: "#B7790B",
        iconColor: "#9D6709",
        icon: ErrorOutline,
    },
    ID: {
        labelId: "document-status-id",
        fallback: "_W dyskusji",
        color: "#347A9A",
        icon: ForumOutlined,
    },
} as const satisfies Record<string, StatusConfigEntry>;

type StatusKey = keyof typeof STATUS_CONFIG;

function normalizeStatus(status: string | null | undefined): string {
    return (status ?? "").toString().trim().toUpperCase();
}

function isKnownStatus(status: string): status is StatusKey {
    return Object.prototype.hasOwnProperty.call(STATUS_CONFIG, status);
}

export const DocumentStatusPill: React.FC<DocumentStatusProps> = ({
    status,
    size = "small",
    fullWidth = false,
    maxWidth,
    truncate = true,
    className,
}) => {
    const theme = useTheme();
    const normalizedStatus = normalizeStatus(status);

    const config: StatusConfigEntry = isKnownStatus(normalizedStatus)
        ? STATUS_CONFIG[normalizedStatus]
        : {
            labelId: "document-status-unknown",
            fallback: normalizedStatus || "_Nieznany status",
            color: "#60717D",
            icon: HelpOutline,
        };

    const StatusIcon = config.icon;
    const isSmall = size === "small";
    const isDark = theme.palette.mode === "dark";

    const height = isSmall ? 24 : 28;
    const iconSize = isSmall ? 17 : 19;
    const fontSize = isSmall ? "0.75rem" : "0.8125rem";

    const accentColor = isDark ? lighten(config.color, 0.45) : config.color;
    const iconColor = isDark
        ? accentColor
        : config.iconColor ?? config.color;

    const backgroundColor = alpha(accentColor, isDark ? 0.16 : 0.10);
    const borderColor = alpha(accentColor, 0.46);

    const localizedLabel = (
        <Localized id={config.labelId}>
            <span>{config.fallback}</span>
        </Localized>
    );

    return (
        <Tooltip
            title={truncate ? localizedLabel : ""}
            arrow
            describeChild
            enterDelay={400}
        >
            <Box
                component="span"
                className={className}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: isSmall ? "6px" : "7px",

                    width: fullWidth ? "100%" : "120px",
                    maxWidth: maxWidth ?? "100%",
                    minWidth: 0,
                    flexShrink: 0,
                    minHeight: height,
                    height: truncate ? height : "auto",

                    pl: isSmall ? "5px" : "7px",
                    pr: isSmall ? "9px" : "11px",
                    py: truncate ? 0 : "4px",

                    bgcolor: backgroundColor,
                    border: "1px solid",
                    borderColor,
                    borderRadius: "5px",

                    boxSizing: "border-box",
                    overflow: "hidden",
                    verticalAlign: "middle",
                }}
            >
                <StatusIcon
                    aria-hidden="true"
                    sx={{
                        color: iconColor,
                        fontSize: iconSize,
                        width: iconSize,
                        height: iconSize,
                        flexShrink: 0,
                    }}
                />

                <Typography
                    component="span"
                    variant="body2"
                    sx={{
                        display: "block",
                        minWidth: 0,
                        flex: fullWidth ? 1 : "0 1 auto",

                        color: theme.palette.text.primary,
                        fontSize,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        letterSpacing: 0,

                        textAlign: "left",
                        textTransform: "none",
                        whiteSpace: truncate ? "nowrap" : "normal",
                        overflow: "hidden",
                        textOverflow: truncate ? "ellipsis" : "clip",
                        overflowWrap: truncate ? "normal" : "anywhere",
                    }}
                >
                    {localizedLabel}
                </Typography>
            </Box>
        </Tooltip>
    );
};